import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared behavior for Sheet and Modal: traps focus inside the dialog while
 * open, closes on Escape, and restores focus to whatever triggered the
 * dialog once it closes. Both components render very differently but need
 * identical a11y mechanics, so it lives here rather than being copy-pasted.
 */
export function useDialogBehavior(open: boolean, onClose: () => void, containerRef: React.RefObject<HTMLElement | null>) {
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Remember whatever had focus before the dialog opened so it can be
    // restored on close — usually the button that triggered it.
    triggerRef.current = document.activeElement as HTMLElement | null;

    // Moving focus is retried across a few frames rather than done once.
    // Two things make a single attempt unreliable: the dialog is not in the
    // DOM when this effect first runs, and it animates in — an element that
    // is still transitioning can silently refuse focus. Without this, a
    // keyboard user is left stranded behind the overlay.
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;

    const tryFocus = () => {
      const container = containerRef.current;

      if (container) {
        // A dialog can name its own starting point with data-autofocus — e.g. a
        // tour's Next button rather than the Skip that happens to come first.
        const target =
          container.querySelector<HTMLElement>("[data-autofocus]:not([disabled])") ??
          container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
          container;
        if (target === container && !container.hasAttribute("tabindex")) {
          // Nothing focusable inside; make the dialog itself focusable so it
          // is announced and Escape still reaches the handler below.
          container.setAttribute("tabindex", "-1");
        }
        target.focus();
        if (container.contains(document.activeElement)) return;
      }

      // Retried on a timer rather than requestAnimationFrame: rAF is throttled
      // in background tabs, which would leave the dialog unfocused. Gives up
      // after ~10 attempts so a genuinely unfocusable dialog cannot spin.
      if (attempts++ < 10) timer = setTimeout(tryFocus, 16);
    };

    timer = setTimeout(tryFocus, 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      // Read the container on each keypress rather than closing over it: the
      // ref is null when this effect runs, and its contents can change while
      // the dialog is open.
      const nodes = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!nodes || nodes.length === 0) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown, true);
      // Restore focus to the trigger only if it's still attached to the DOM.
      if (triggerRef.current && document.contains(triggerRef.current)) {
        triggerRef.current.focus();
      }
    };
    // Intentionally keyed on `open` alone: re-running when the refs change
    // would re-trap focus mid-interaction.
  }, [open]);
}
